import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import Navbar from '../../Pages/Navbar/AgencyNavbar';
import Footer from "../../Footer/Footer"
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl';
import './ViewJobDetails.css';

function JobDetails() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { jobId } = useParams();
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const userId = user?.id;
    const [job, setJob] = useState(state?.job || null);
    const [applied, setApplied] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showRegisterPopup, setShowRegisterPopup] = useState(false);
    const [showLoginPopup, setShowLoginPopup] = useState(false);
    const [hasResume, setHasResume] = useState(false);
    const [relatedJobs, setRelatedJobs] = useState([]);

    const safeParseArray = (value) => {
        try {
            if (!value) return [];
            if (Array.isArray(value)) return value;
            return JSON.parse(value);
        } catch (e) {
            return [];
        }
    };

    useEffect(() => {
        const fetchJobDetails = async () => {
            try {
                // If job not passed via state, fetch it by ID
                if (!job && jobId) {
                    const response = await fetch(`${BaseUrl}/api/employer/${jobId}`);
                    if (!response.ok) {
                        throw new Error('Failed to fetch job details');
                    }
                    const data = await response.json();
                    setJob(data);
                }

                // Check if user has applied and has resume
                if (user.id) {
                    // Check application status
                    const applicationResponse = await fetch(`${BaseUrl}/api/applications?userid=${user.id}&jobid=${jobId || job?.id}`);
                    const applicationData = await applicationResponse.json();
                    if (applicationResponse.ok && applicationData.length > 0) {
                        setApplied(true);
                    }

                    // Check resume status
                    const resumeResponse = await fetch(`${BaseUrl}/api/job-seeker?user_id=${user.id}`);
                    const resumeData = await resumeResponse.json();
                    setHasResume(resumeData.length > 0);
                }
            } catch (err) {
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchJobDetails();
    }, [jobId, job, user.id]);

    useEffect(() => {
        const fetchRelatedJobs = async () => {
            if (!job?.domestic_worker_category) return;

            try {
                const response = await fetch(`${BaseUrl}/api/employer/by-user?user_id=${userId}`);
                if (!response.ok) {
                    throw new Error('Failed to fetch jobs');
                }
                let data = await response.json();

                // Filter jobs where status is 'Approved' and category matches
                data = data.filter(relatedJob =>
                    relatedJob.status === 'Approved' &&
                    relatedJob.domestic_worker_category === job.domestic_worker_category &&
                    relatedJob.id !== (jobId || job?.id) // Exclude current job
                ).slice(0, 4); // Limit to 4 jobs

                // Safely parse relevant fields
                data = data.map(relatedJob => ({
                    ...relatedJob,
                    preferred_candidates_country: safeParseArray(relatedJob.preferred_candidates_country),
                    candidates_country_experience: safeParseArray(relatedJob.candidates_country_experience),
                    main_skills: safeParseArray(relatedJob.main_skills),
                    cooking_skills: safeParseArray(relatedJob.cooking_skills),
                    other_skills: safeParseArray(relatedJob.other_skills),
                    preferred_years_of_experience: safeParseArray(relatedJob.preferred_years_of_experience),
                    offer_for_selected_candidates: safeParseArray(relatedJob.offer_for_selected_candidates),
                    preferred_language_for_worker: safeParseArray(relatedJob.preferred_language_for_worker),
                    profile_photo_url: job.profile_photo
                        ? `${BaseUrl}${job.profile_photo}`
                        : null
                }));

                setRelatedJobs(data);
            } catch (err) {
                console.error('Error fetching related jobs:', err);
            }
        };

        if (job) {
            fetchRelatedJobs();
        }
    }, [job, jobId]);

    const handleBackToList = () => {
        navigate('/a-jobposting');
    };

    const handleApply = async () => {
        if (!user.id) {
            Swal.fire({
                title: 'Registration Required',
                text: 'You need to register or login to apply for this job',
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Register',
                cancelButtonText: 'Login',
                customClass: {
                    popup: 'registration-popup'
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/register');
                } else if (result.dismiss === Swal.DismissReason.cancel) {
                    navigate('/login');
                }
            });
            return;
        }

        if (!hasResume) {
            Swal.fire({
                title: 'Resume Required',
                text: 'You need to update your resume before applying for jobs',
                icon: 'info',
                confirmButtonText: 'Update resume',
                confirmButtonColor: '#28a745'
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/personalinfo');
                }
            });
            return;
        }

        try {
            const payload = {
                userid: user.id,
                empuserid: job.user_id,
                jobid: job.id,
            };

            const response = await fetch(`${BaseUrl}/api/applications`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'You have successfully applied for this job',
                    confirmButtonColor: '#28a745'
                });
                setApplied(true);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: data.error || 'Failed to apply for job',
                    confirmButtonColor: '#d33'
                });
            }
        } catch (err) {
            console.error('Error while applying:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An error occurred while applying: ' + err.message,
                confirmButtonColor: '#d33'
            });
        }
    };

    const closeRegisterPopup = () => {
        setShowRegisterPopup(false);
    };

    const closeLoginPopup = () => {
        setShowLoginPopup(false);
    };

    const handleLoginSuccess = async () => {
        closeLoginPopup();
        const updatedUser = JSON.parse(localStorage.getItem('user')) || {};

        setLoading(true);
        try {
            if (updatedUser.id && job?.id) {
                const applicationResponse = await fetch(`${BaseUrl}/api/applications?userid=${updatedUser.id}&jobid=${job.id}`);
                const applicationData = await applicationResponse.json();
                if (applicationResponse.ok && applicationData.length > 0) {
                    setApplied(true);
                }

                const resumeResponse = await fetch(`${BaseUrl}/api/job-seeker?user_id=${updatedUser.id}`);
                const resumeData = await resumeResponse.json();
                setHasResume(resumeData.length > 0);
            }
        } catch (err) {
            console.error('Error checking application status:', err);
        } finally {
            setLoading(false);
        }
    };

    const safeJoin = (array, separator = ', ') => {
        if (!array) return 'N/A';
        return Array.isArray(array) ? array.join(separator) : array;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString.includes('T') ? dateString : dateString + 'T00:00:00');
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            console.error('Error formatting date:', e);
            return 'N/A';
        }
    };

    if (!job) {
        return (
            <>
                <Navbar />
                <div className="text-center mt-5" style={{ marginTop: '100px' }}>
                    <div className="text-center">Job details not found</div>
                    <button
                        className="btn btn-outline-secondary mt-3"
                        onClick={handleBackToList}
                    >
                        Back to List
                    </button>
                </div>
            </>
        );
    }

    return (
        <>
            <Navbar />
            <section className="agency blog blog-sec blog-sidebar pb-5 blog-list sider" style={{ marginTop: '80px' }}>
                <Container>
                    <Row>
                        <Col md={12} lg={9} className="pe-lg-4">
                            <div className="product-wrapper-grid list-view">
                                <Row>
                                    <Col xs={12} className="col-12_padding_Set">
                                        <div className="agency-box position-relative">
                                            <div className="agency-header-container">
                                                <picture>
                                                    <source media="(max-width:576px)" srcSet="https://cdn.helperplace.com/front-app/assets/images/misc/5-sm.webp" />
                                                    <source media="(max-width:768px)" srcSet="https://cdn.helperplace.com/front-app/assets/images/misc/5-md.webp" />
                                                    <img
                                                        src="https://cdn.helperplace.com/front-app/assets/images/misc/5.jpg"
                                                        alt="Header"
                                                        className="agency_header"
                                                    />
                                                </picture>
                                                <div className="agency_header_White_opcity"></div>

                                                <div className="profile-section">
                                                    <div className="profile-image-wrapper">
                                                        <img
                                                            src={job.profile_photo_url || '/fallback-user.png'}
                                                            alt="Profile"
                                                            className="profile-image"
                                                        />
                                                    </div>

                                                    <div className="badge-group">
                                                        <label className="label_blue">
                                                            {job.posted_by?.toLowerCase().includes('direct') ? 'Direct' : 'Agency'}
                                                        </label>
                                                        <span className="top-active">Very Active</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="product-box">
                                                <div className="hp-candidate-wrapper">
                                                    <div className="job-title-section">
                                                        <h1 className="mb-0 listing-about-title p-0" style={{ wordBreak: 'break-word' }}>
                                                            {job.job_title}
                                                        </h1>
                                                        <p className="mb-2 p-0 text-left listing-about-sub-title">
                                                            {job.adults || 'N/A'} adults + {job.children || 'N/A'} kids | {job.nationality || 'N/A'} | Posted by employer
                                                        </p>
                                                    </div>

                                                    <div className="jobdetails-buttons">
                                                        <Button
                                                            variant="secondary"
                                                            className="hp-cancel-jobdetails"
                                                            onClick={handleBackToList}
                                                        >
                                                            Back
                                                        </Button>
                                                        {/* Uncomment if you want the Apply button */}
                                                        {/* <button
        className="hp-apply-jobdetails btn-apply-button-alljobdetails-unique"
        onClick={handleApply}
        disabled={applied || loading}
      >
        {loading ? 'Checking...' : (applied ? 'Applied' : 'APPLY')}
      </button> */}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Job Requirement Section */}
                                <Row className="custom-header-box-viewjob">
                                    <Col className="mx-auto">
                                        <h5 className="text-light ar">Job Requirement</h5>
                                    </Col>
                                </Row>
                                <Row className="job-requirement">
                                    <Col className="mx-auto">
                                        <Row className="ar">
                                            <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                                <h3 className="footer-experience">
                                                    <svg className="icons" height="17" width="17">
                                                        <use height="14" width="14" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#user"></use>
                                                    </svg>
                                                    {job.domestic_worker_category || 'N/A'} <span> |</span>
                                                    <span> {job.job_type || 'N/A'} </span>
                                                </h3>
                                            </Col>
                                            <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                                <h3 className="footer-experience">
                                                    <svg className="icons" height="17" width="17">
                                                        <use height="14" width="14" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#calendar"></use>
                                                    </svg>
                                                    Start Before: {formatDate(job.job_starting_date)}
                                                </h3>
                                            </Col>
                                            <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                                <h3 className="footer-experience">
                                                    <svg className="icons" height="17" width="17">
                                                        <use height="14" width="14" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#map"></use>
                                                    </svg>
                                                    {job.working_city || 'N/A'}, {job.state_or_province || 'N/A'}
                                                </h3>
                                            </Col>
                                            <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                                <h3 className="footer-experience">
                                                    <svg className="icons" height="17" width="17">
                                                        <use height="16" width="16" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#file"></use>
                                                    </svg>
                                                    Contract Status: {job.contract_status || 'N/A'}
                                                </h3>
                                            </Col>
                                            <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                                <h3 className="footer-experience">
                                                    <svg className="icons" height="17" width="17">
                                                        <use height="16" width="16" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#bed-person"></use>
                                                    </svg>
                                                    Accommodation: {job.accommodation_type || 'N/A'}
                                                </h3>
                                            </Col>
                                            <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                                <h3 className="footer-experience">
                                                    <svg className="icons" height="17" width="17">
                                                        <use height="15" width="15" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#day_off"></use>
                                                    </svg>
                                                    Day Off: <span>{job.day_off || 'N/A'}</span>
                                                </h3>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                {/* Job Description Section */}
                                <Row className="custom-header-box-viewjob">
                                    <Col className="mx-auto">
                                        <h5 className="text-light ar">Job Description</h5>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="mx-auto jOb_description description_tag ar">
                                        <div style={{ wordBreak: 'break-word' }}>
                                            {job.job_description}
                                        </div>
                                    </Col>
                                </Row>

                                {/* Required Skills Section */}
                                <Row className="custom-header-box-viewjob">
                                    <Col className="mx-auto">
                                        <h5 className="text-light ar">Required Skills / Duties</h5>
                                    </Col>
                                </Row>
                                <Row className="yoga mt-3 event">
                                    {/* Language Skills */}
                                    <Col className="ar">
                                        <div className="col-11 phone_pedding mx-auto">
                                            <div className="event-container event-container_footer_margin_custom d-flex">
                                                <div className="yoga-circle">
                                                    <picture>
                                                        <source width="35px" height="35px" media="(max-width:992px)" srcSet="https://cdn.helperplace.com/skill_cat/1_1599642484.webp" />
                                                        <img width="40px" height="40px" src="https://cdn.helperplace.com/skill_cat/1_1599642484.webp" alt="Language skills" />
                                                    </picture>
                                                </div>
                                                <div className="event-info">
                                                    <h3 className="primary ar"> Language: </h3>
                                                    {job.preferred_language_for_worker?.length > 0 ? (
                                                        job.preferred_language_for_worker.map((lang, index) => (
                                                            <h4 key={index} className="float-left float-left-ar color_1"> {lang} </h4>
                                                        ))
                                                    ) : (
                                                        <h4 className="float-left float-left-ar color_1">N/A</h4>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Main Skills */}
                                    <Col xs={11} className="ar">
                                        <div className="col-11 phone_pedding mx-auto">
                                            <div className="event-container event-container_footer_margin_custom d-flex">
                                                <div className="yoga-circle">
                                                    <picture>
                                                        <source width="35px" height="35px" media="(max-width:992px)" srcSet="https://cdn.helperplace.com/skill_cat/4_1599643968.webp" />
                                                        <img width="40px" height="40px" src="https://cdn.helperplace.com/skill_cat/4_1599643968.webp" alt="Main skills" />
                                                    </picture>
                                                </div>
                                                <div className="event-info">
                                                    <h3 className="primary ar"> Main Skills: </h3>
                                                    {job.main_skills?.length > 0 ? (
                                                        job.main_skills.map((skill, index) => (
                                                            <h4 key={index} className="float-left float-left-ar color_2"> {skill} </h4>
                                                        ))
                                                    ) : (
                                                        <h4 className="float-left float-left-ar color_2">N/A</h4>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Cooking Skills */}
                                    <Col xs={11} className="ar">
                                        <div className="col-11 phone_pedding mx-auto">
                                            <div className="event-container event-container_footer_margin_custom d-flex">
                                                <div className="yoga-circle">
                                                    <picture>
                                                        <source width="35px" height="35px" media="(max-width:992px)" srcSet="https://cdn.helperplace.com/skill_cat/2_1599644151.webp" />
                                                        <img width="40px" height="40px" src="https://cdn.helperplace.com/skill_cat/2_1599644151.webp" alt="Cooking skills" />
                                                    </picture>
                                                </div>
                                                <div className="event-info">
                                                    <h3 className="primary ar"> Cooking Skills: </h3>
                                                    {job.cooking_skills?.length > 0 ? (
                                                        job.cooking_skills.map((skill, index) => (
                                                            <h4 key={index} className="float-left float-left-ar color_3"> {skill} </h4>
                                                        ))
                                                    ) : (
                                                        <h4 className="float-left float-left-ar color_3">N/A</h4>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Other Skills */}
                                    <Col xs={11} className="ar">
                                        <div className="col-11 phone_pedding mx-auto">
                                            <div className="event-container event-container_footer_margin_custom d-flex">
                                                <div className="yoga-circle">
                                                    <picture>
                                                        <source width="35px" height="35px" media="(max-width:992px)" srcSet="https://cdn.helperplace.com/skill_cat/3_1599642528.webp" />
                                                        <img width="40px" height="40px" src="https://cdn.helperplace.com/skill_cat/3_1599642528.webp" alt="Other skills" />
                                                    </picture>
                                                </div>
                                                <div className="event-info">
                                                    <h3 className="primary ar"> Other Skills: </h3>
                                                    {job.other_skills?.length > 0 ? (
                                                        job.other_skills.map((skill, index) => (
                                                            <h4 key={index} className="float-left float-left-ar color_4"> {skill} </h4>
                                                        ))
                                                    ) : (
                                                        <h4 className="float-left float-left-ar color_4">N/A</h4>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <span className="float-right float-left-ar primary pointer mr-2 ml-2"> Report this Job </span>
                            </div>
                        </Col>

                        {/* Sidebar with Related Jobs */}
                        <Col md={12} lg={3} className="collection-filter-block resume-right-block">
                            <div className="sticky-top" style={{ top: '90px' }}>
                                <h4 className="mb-4">Similar Jobs</h4>
                                {relatedJobs.length > 0 ? (
                                    relatedJobs.map((relatedJob) => (
                                        <div key={relatedJob.id} className="mb-4">
                                            <div className="blog-agency">
                                                <div className="blog-contain">
                                                    <div className="img-container">
                                                        <div
                                                            className="blog-info-resume"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => navigate(`/jobdetails`, { state: { job: relatedJob } })}
                                                        >
                                                            <picture>
                                                                <source srcSet="https://cdn.helperplace.com/misc/jpi/3/Dad%202%20kids-sm.webp" />
                                                                <img
                                                                    width="70px"
                                                                    height="70px"
                                                                    className="related-profile-picture"
                                                                    src={relatedJob.employer_profile_pic || "https://cdn.helperplace.com/misc/jpi/default_family.jpg"}
                                                                    alt={relatedJob.job_title}
                                                                />
                                                            </picture>
                                                            <div className="ar">
                                                                <h4 className="blog-head fixed-1-line">{relatedJob.job_title}</h4>
                                                                <p className="card-text">
                                                                    <span className="related-employer-type">Family</span> - {relatedJob.nationality || 'N/A'}
                                                                </p>
                                                                <p className="card-text">
                                                                    <svg className="icons" height="17" width="17">
                                                                        <use height="11" width="11" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#map"></use>
                                                                    </svg>
                                                                    {relatedJob.working_city || 'N/A'}, {relatedJob.state_or_province || 'N/A'}
                                                                </p>
                                                                <p className="card-text">
                                                                    <svg className="icons" height="17" width="17">
                                                                        <use height="12" width="12" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#certificate"></use>
                                                                    </svg>
                                                                    {relatedJob.domestic_worker_category || 'N/A'} | {relatedJob.job_type || 'N/A'}
                                                                </p>
                                                                <p className="card-text">
                                                                    <svg className="icons" height="17" width="17">
                                                                        <use height="12" width="12" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#calendar"></use>
                                                                    </svg>
                                                                    From {formatDate(relatedJob.job_starting_date)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p>No similar jobs found</p>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Registration Popup */}
            {showRegisterPopup && (
                <div className="modal-backdrop" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 1050,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <div className="modal-content" style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '0px',
                        maxWidth: '500px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5>Register to Apply</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={closeRegisterPopup}
                                aria-label="Close"
                            ></button>
                        </div>
                        {/* <Register onSuccess={closeRegisterPopup} /> */}
                    </div>
                </div>
            )}

            {/* Login Popup */}
            {showLoginPopup && (
                <div className="modal-backdrop" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 1050,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <div className="modal-content" style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '0px',
                        maxWidth: '500px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5>Login to Apply</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={closeLoginPopup}
                                aria-label="Close"
                            ></button>
                        </div>
                        {/* <Login onSuccess={handleLoginSuccess} /> */}
                    </div>
                </div>
            )}
            <Footer />
        </>
    );
}

export default JobDetails;
